import i18n from 'i18next';

import type { WidgetDataSource } from '../gigawidget/types';
import { dataSources } from './data-sources';
import type { GigawidgetDataSources, GigawidgetSettings } from './util';
import { getString } from './util';

export const settingsToTitle = <
  T extends keyof GigawidgetDataSources = keyof GigawidgetDataSources,
>(
  settings: Omit<GigawidgetSettings<T>, 'title'>,
  /**
   * Friendly labels for custom-attribute fields keyed by attribute id. Standard
   * fields resolve via the data source's i18n keys; custom attributes (the only
   * aggregatable ones being Number fields) aren't in that map, so without this
   * the auto-title would show the raw attribute id (e.g. `1771_number`).
   */
  customAttributeFieldLabels?: Record<string, string>
) => {
  const dataSource = dataSources[settings.dataSource] as WidgetDataSource;
  const categoryGetter = dataSource.categoryGetters.find(
    (getter) => getter.id === settings.categoryGetter
  );
  const subCategoryGetter = dataSource.categoryGetters.find(
    (getter) => getter.id === settings.subCategoryGetter
  );
  // titleCase preserves lowercase "by" in relational names like
  // "Actions by Controls" while still capitalising multi-word single
  // sources (RSP-5300).
  const entityNamePlural = i18n.format(
    i18n.t(dataSource.entityNamePlural),
    'titleCase'
  );
  const entityNameSingular = i18n.format(
    i18n.t(dataSource.entityNameSingular),
    'titleCase'
  );
  if (!categoryGetter) {
    return `${entityNamePlural}`;
  }

  if (subCategoryGetter) {
    return `${entityNamePlural} ${getString(
      subCategoryGetter.name
    )} by ${getString(categoryGetter.name)}`;
  }

  const translationKeys = i18n.t(dataSource.fields, { returnObjects: true });
  const aggregationFieldLabel =
    translationKeys[
      settings.aggregationField as keyof typeof translationKeys
    ] ??
    customAttributeFieldLabels?.[settings.aggregationField as string] ??
    settings.aggregationField;

  if (settings.aggregationType === 'sum') {
    return `Sum of ${entityNameSingular} ${aggregationFieldLabel} by ${getString(
      categoryGetter.name
    )}`;
  }
  if (settings.aggregationType === 'mean') {
    return `Mean of ${entityNameSingular} ${aggregationFieldLabel} by ${getString(
      categoryGetter.name
    )}`;
  }

  return `${entityNamePlural} by ${getString(categoryGetter.name)}`;
};
